import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI } from '../utils/api';
import './Errors.css';

function Errors() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [unresolvedOnly, setUnresolvedOnly] = useState(searchParams.get('unresolved') === 'true');
  const [hours, setHours] = useState(searchParams.get('hours') || '');
  const [selectedError, setSelectedError] = useState(null);
  const limit = 20;

  const fetchErrors = useCallback(async () => {
    try {
      setLoading(true);
      const [errorsData, statsData] = await Promise.all([
        adminAPI.getErrors({
          skip: page * limit,
          limit,
          category: category || undefined,
          unresolvedOnly,
          hours: hours ? parseInt(hours) : undefined
        }),
        adminAPI.getErrorStats(24)
      ]);
      setErrors(errorsData);
      setStats(statsData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load errors');
    } finally {
      setLoading(false);
    }
  }, [page, category, unresolvedOnly, hours]);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  const handleResolve = async (errorId) => {
    try {
      await adminAPI.resolveError(errorId);
      fetchErrors();
      setSelectedError(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to resolve error');
    }
  };

  const handleDelete = async (errorId) => {
    if (!confirm('Are you sure you want to delete this error log?')) return;
    try {
      await adminAPI.deleteError(errorId);
      fetchErrors();
      setSelectedError(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete error');
    }
  };

  const openErrorDetail = async (errorItem) => {
    try {
      const detail = await adminAPI.getErrorDetail(errorItem.id);
      setSelectedError(detail);
    } catch (err) {
      console.error('Failed to fetch error detail:', err);
    }
  };

  const getCategoryBadgeClass = (cat) => {
    switch (cat) {
      case 'rate_limit': return 'warning';
      case 'auth': return 'info';
      case 'validation': return 'secondary';
      case 'timeout': return 'warning';
      case 'network': return 'danger';
      case 'database': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <div className="errors-page">
      <div className="page-header">
        <h1 className="page-title">Error Logs</h1>
        <p className="page-subtitle">Monitor and resolve application errors</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Errors (24h)</div>
            <div className="stat-value">{stats.total_errors}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Unresolved</div>
            <div className="stat-value">{stats.unresolved_count}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Rate Limits</div>
            <div className="stat-value">{stats.by_category?.rate_limit || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Auth Errors</div>
            <div className="stat-value">{stats.by_category?.auth || 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <select
          className="form-input filter-select"
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(0); }}
        >
          <option value="">All Categories</option>
          <option value="rate_limit">Rate Limit</option>
          <option value="auth">Auth</option>
          <option value="validation">Validation</option>
          <option value="timeout">Timeout</option>
          <option value="network">Network</option>
          <option value="database">Database</option>
          <option value="unknown">Unknown</option>
        </select>

        <select
          className="form-input filter-select"
          value={hours}
          onChange={(e) => { setHours(e.target.value); setPage(0); }}
        >
          <option value="">All Time</option>
          <option value="1">Last Hour</option>
          <option value="6">Last 6 Hours</option>
          <option value="24">Last 24 Hours</option>
          <option value="72">Last 3 Days</option>
          <option value="168">Last Week</option>
        </select>

        <label className="checkbox-filter">
          <input
            type="checkbox"
            checked={unresolvedOnly}
            onChange={(e) => { setUnresolvedOnly(e.target.checked); setPage(0); }}
          />
          <span>Unresolved Only</span>
        </label>

        <button className="btn btn-secondary" onClick={fetchErrors}>
          Refresh
        </button>
      </div>

      {/* Errors Table */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading errors...</span>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchErrors}>Retry</button>
        </div>
      ) : errors.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 15h8M9 9h.01M15 9h.01" />
            </svg>
          </div>
          <h3>No errors found</h3>
          <p>No errors match your current filters</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Message</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((err) => (
                  <tr key={err.id} className={err.is_resolved ? '' : 'unresolved'}>
                    <td>
                      <div className="time-cell">
                        <div className="time-date">
                          {new Date(err.created_at).toLocaleDateString()}
                        </div>
                        <div className="time-hour">
                          {new Date(err.created_at).toLocaleTimeString()}
                        </div>
                      </div>
                    </td>
                    <td>
                      <code className="error-type">{err.error_type}</code>
                    </td>
                    <td>
                      <span className={`badge badge-${getCategoryBadgeClass(err.error_category)}`}>
                        {err.error_category || 'unknown'}
                      </span>
                    </td>
                    <td>
                      <div className="message-cell" title={err.message}>
                        {err.message.substring(0, 80)}
                        {err.message.length > 80 && '...'}
                      </div>
                    </td>
                    <td>
                      <div className="user-cell">
                        {err.user_email || err.user_id || '-'}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${err.is_resolved ? 'badge-success' : 'badge-danger'}`}>
                        {err.is_resolved ? 'Resolved' : 'Open'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => openErrorDetail(err)}
                        >
                          View
                        </button>
                        {!err.is_resolved && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleResolve(err.id)}
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
            >
              Previous
            </button>
            <span className="pagination-info">Page {page + 1}</span>
            <button
              className="pagination-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={errors.length < limit}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Error Detail Modal */}
      {selectedError && (
        <div className="modal-overlay" onClick={() => setSelectedError(null)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Error Details</h3>
              <button className="modal-close" onClick={() => setSelectedError(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="error-detail-grid">
                <div className="detail-item">
                  <label>Error ID</label>
                  <code>{selectedError.error_id}</code>
                </div>
                <div className="detail-item">
                  <label>Time</label>
                  <span>{new Date(selectedError.created_at).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Type</label>
                  <code>{selectedError.error_type}</code>
                </div>
                <div className="detail-item">
                  <label>Category</label>
                  <span className={`badge badge-${getCategoryBadgeClass(selectedError.error_category)}`}>
                    {selectedError.error_category || 'unknown'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span className={`badge ${selectedError.is_resolved ? 'badge-success' : 'badge-danger'}`}>
                    {selectedError.is_resolved ? 'Resolved' : 'Open'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>User</label>
                  <span>{selectedError.user_email || selectedError.user_id || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Request</label>
                  <span>{selectedError.request_method} {selectedError.request_path || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>IP Address</label>
                  <span>{selectedError.ip_address || '-'}</span>
                </div>
              </div>

              <div className="detail-section">
                <label>Message</label>
                <pre className="error-message-box">{selectedError.message}</pre>
              </div>

              {selectedError.stack_trace && (
                <div className="detail-section">
                  <label>Stack Trace</label>
                  <pre className="stack-trace-box">{selectedError.stack_trace}</pre>
                </div>
              )}

              {selectedError.context && (
                <div className="detail-section">
                  <label>Context</label>
                  <pre className="context-box">{selectedError.context}</pre>
                </div>
              )}

              {selectedError.resolution_notes && (
                <div className="detail-section">
                  <label>Resolution Notes</label>
                  <p>{selectedError.resolution_notes}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {!selectedError.is_resolved && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleResolve(selectedError.id)}
                >
                  Mark as Resolved
                </button>
              )}
              <button
                className="btn btn-danger"
                onClick={() => handleDelete(selectedError.id)}
              >
                Delete
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedError(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Errors;
